import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '../../../layouts/DashboardShell';
import { clientSideLinks } from '../clientRoutes';
import { fetchExpiredJobs, repostJob } from '../../../services/api';
import CardIcon from '../../../shared/components/icons/CardIcon';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} at 23:59`;
};

const toDateInputValue = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isFutureDate = (value) => {
  if (!value) return false;
  const selected = new Date(`${value}T23:59:59`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected.getTime() > today.getTime();
};

function ExpiredJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalJob, setModalJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [repostError, setRepostError] = useState('');

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await fetchExpiredJobs();
      setJobs(Array.isArray(data) ? data : data?.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load expired jobs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const minAllowedDate = useMemo(() => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    today.setHours(0, 0, 0, 0);
    return toDateInputValue(today);
  }, []);

  const resetRepostState = () => {
    setModalJob(null);
    setSelectedDate('');
    setShowConfirm(false);
    setSubmitting(false);
    setRepostError('');
  };

  const openRepostModal = (job) => {
    setModalJob(job);
    setSelectedDate('');
    setShowConfirm(false);
    setRepostError('');
  };

  const handleConfirmRepost = async () => {
    if (!modalJob || !selectedDate || !isFutureDate(selectedDate)) {
      return;
    }

    setSubmitting(true);
    setRepostError('');
    try {
      const result = await repostJob(modalJob.id, selectedDate);
      const nextExpiry = result?.expiresAt || selectedDate;
      setSuccessState({
        title: modalJob.title,
        expiresAt: nextExpiry,
      });
      resetRepostState();
      await loadJobs();
    } catch (err) {
      setRepostError(err.message || 'Unable to repost this job.');
    } finally {
      setSubmitting(false);
    }
  };

  if (successState) {
    return (
      <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
        <div className="space-y-6">
          <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
            <div className="flex items-center gap-3 text-emerald-600">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100"><CardIcon name="check" className="h-6 w-6" /></span>
              <h1 className="text-2xl font-bold text-navy">Job Reposted Successfully</h1>
            </div>

            <div className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
              <p className="font-semibold">Your job is now active.</p>
              <p className="mt-2">{successState.title}</p>
              <p className="mt-1">Expires: {formatDateTime(successState.expiresAt)}</p>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setSuccessState(null);
                  navigate('/client/jobs');
                }}
                className="rounded-2xl bg-navy px-5 py-3 text-sm font-semibold text-white"
              >
                <span className="inline-flex items-center gap-2"><CardIcon name="briefcase" />View Active Job</span>
              </button>
            </div>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
      <div className="space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
          <h1 className="flex items-center gap-3 text-2xl font-bold text-navy sm:text-3xl"><CardIcon name="warning" className="h-7 w-7" />Expired Jobs</h1>
          <p className="mt-2 text-sm text-slate-600">
            Jobs that expired before they were filled. You can repost a job by choosing a new expiry date.
          </p>
          <p className="mt-4 text-sm font-semibold text-slate-700">
            {loading ? 'Loading...' : `${jobs.length} Expired Job${jobs.length === 1 ? '' : 's'}`}
          </p>
        </div>

        {error && <div className="rounded-3xl bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">{error}</div>}

        {loading ? (
          <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading expired jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="rounded-3xl bg-white p-8 text-center text-slate-500 shadow-sm">No expired jobs found.</div>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job) => {
              const title = job.title || 'Untitled job';
              const description = job.description || 'No description provided.';
              const location = [job.area, job.city].filter(Boolean).join(', ') || 'Location unavailable';
              const skill = job.skill?.name || 'General work';
              const category = job.skill?.category?.name || 'Uncategorized';
              const statusLabel = String(job.urgency || 'normal').toUpperCase();
              const expiryDate = job.expiredAt?.full || job.expiresAt || 'N/A';
              const postedDate = job.postedAt?.full || job.createdAt || 'N/A';

              return (
                <article
                  key={job.id}
                  className="cursor-pointer rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-slate-300 hover:shadow-md"
                  onClick={() => navigate(`/client/expired-jobs/${job.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/client/expired-jobs/${job.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-rose-700">
                      <CardIcon name="warning" className="h-3.5 w-3.5" />Expired
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500"><CardIcon name="calendar" />{formatDate(postedDate)}</span>
                  </div>

                  <div className="mt-6">
                    <h3 className="text-2xl font-bold uppercase tracking-tight text-navy">{title}</h3>
                    <p className="mt-3 text-sm font-semibold text-slate-700">{skill}</p>
                    <p className="mt-1 text-sm text-slate-600">{category}</p>
                  </div>

                  <div className="mt-6 space-y-3 text-sm text-slate-700">
                    <p className="font-semibold uppercase tracking-[0.12em] text-slate-500">{description}</p>
                    <p className="inline-flex items-center gap-1.5"><CardIcon name="location" />{location}</p>
                    <p className="inline-flex items-center gap-1.5"><CardIcon name="money" />K{job.budget ?? 0}{job.isNegotiable ? ' · Negotiable' : ''}</p>
                    <p className="inline-flex items-center gap-1.5"><CardIcon name="people" />{job.requiredPeople ?? 1} person{(job.requiredPeople ?? 1) === 1 ? '' : 's'}</p>
                    <p className="inline-flex items-center gap-1.5"><CardIcon name="clock" />{(job.workType || 'PART_TIME').replace('_', ' ')}</p>
                    <p className="inline-flex items-center gap-1.5"><CardIcon name="flame" />{statusLabel}</p>
                    <div className="border-t border-slate-100 pt-4 text-slate-600">
                      <p className="inline-flex items-center gap-1.5"><CardIcon name="calendar" />Posted: <span className="font-semibold text-slate-800">{formatDate(postedDate)}</span></p>
                      <p className="mt-2 inline-flex items-center gap-1.5"><CardIcon name="calendar" />Expired: <span className="font-semibold text-slate-800">{formatDate(expiryDate)}</span></p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end border-t border-slate-100 pt-5">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openRepostModal(job);
                      }}
                      className="rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      <span className="inline-flex items-center gap-2"><CardIcon name="refresh" />Repost Job</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {modalJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <h2 className="inline-flex items-center gap-2 text-xl font-bold text-navy"><CardIcon name="refresh" />Repost Job</h2>
              <button type="button" onClick={resetRepostState} className="text-2xl leading-none text-slate-500">×</button>
            </div>

            {!showConfirm ? (
              <div className="mt-6 space-y-5">
                {repostError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {repostError}
                  </div>
                )}

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-lg font-bold text-navy">{modalJob.title}</p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{[modalJob.area, modalJob.city].filter(Boolean).join(', ') || 'Location unavailable'}</p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="money" />K{modalJob.budget ?? 0}{modalJob.isNegotiable ? ' · Negotiable' : ''}</p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="people" />{modalJob.requiredPeople ?? 1} person · {(modalJob.workType || 'PART_TIME').replace('_', ' ')}</p>
                </div>

                <p className="text-sm leading-6 text-slate-600">
                  This job will be reposted using the same details as the original job. You only need to select a new expiry date.
                </p>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><CardIcon name="calendar" />New expiry date</label>
                  <input
                    type="date"
                    min={minAllowedDate}
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                  />
                  <p className="mt-2 text-xs text-slate-500">Select a future expiry date.</p>
                  {!selectedDate && (
                    <p className="mt-2 text-xs text-rose-600">Please select a new expiry date.</p>
                  )}
                  {selectedDate && !isFutureDate(selectedDate) && (
                    <p className="mt-2 text-xs text-rose-600">Please choose a future date.</p>
                  )}
                </div>

                <p className="text-sm text-slate-500">
                  The job will remain active until {selectedDate ? formatDateTime(`${selectedDate}T23:59:59`) : 'your selected date'}.
                </p>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={resetRepostState} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700">Cancel</button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedDate || !isFutureDate(selectedDate)) {
                        return;
                      }
                      setShowConfirm(true);
                    }}
                    disabled={!selectedDate || !isFutureDate(selectedDate)}
                    className="rounded-2xl bg-navy px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="inline-flex items-center gap-2"><CardIcon name="refresh" />Repost Job</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-5">
                {repostError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {repostError}
                  </div>
                )}

                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-500">Confirm Repost</p>
                  <h3 className="mt-3 text-2xl font-bold text-navy">{modalJob.title}</h3>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">You are about to repost:</p>
                  <p className="mt-2">{modalJob.title}</p>
                  <p className="mt-3">New expiry: <span className="font-semibold text-slate-900">{formatDateTime(`${selectedDate}T23:59:59`)}</span></p>
                  <p className="mt-3 text-slate-600">All other job details will remain unchanged.</p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowConfirm(false)} disabled={submitting} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60">Cancel</button>
                  <button type="button" onClick={handleConfirmRepost} disabled={submitting} className="rounded-2xl bg-navy px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                    <span className="inline-flex items-center gap-2"><CardIcon name={submitting ? 'refresh' : 'check'} />{submitting ? 'Reposting...' : 'Confirm Repost'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

export default ExpiredJobsPage;
